import {
	Component,
	OnInit,
	TemplateRef,
	ViewChild,
	ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { animate, style, transition, trigger } from '@angular/animations';
import {
	NgbAlertModule,
	NgbDatepickerModule,
	NgbDropdownModule,
	NgbModal,
	NgbModalRef,
	NgbNavModule,
} from '@ng-bootstrap/ng-bootstrap';
import { LoginService } from '../../login-service/login.service';
import { NotificationService } from '../../notification-service/notification.service';
import {
	CommonErrorMessage,
	ErrorResponse,
	LoginInfo,
	MpRegisterRequest,
	OrgReturn,
	Roles,
	userReturn,
	UserSettings,
	UserUpdateRequest,
} from '@meta-press/shared-plugins';
import { catchError, map, of, switchMap, tap, throwError } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { BackendService } from '../../backend-service/backend.service';
import { AiService } from '../../services/ai/ai.service';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
	selector: 'app-user-management',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		NgbDatepickerModule,
		NgbAlertModule,
		NgbDropdownModule,
		NgbNavModule,
	],
	templateUrl: './user-management.component.html',
	styleUrl: './user-management.component.scss',
	animations: [
		trigger('fadeInImmediately', [
			transition(':enter', [
				style({ opacity: 0 }),
				animate('0.35s', style({ opacity: 1 })),
			]),
		]),
	],
})
export class UserManagementComponent implements OnInit {
	@ViewChild('addUserModal') addUserModalTemplate!: TemplateRef<unknown>;
	@ViewChild('editUserModal') editUserModalTemplate!: TemplateRef<unknown>;
	@ViewChild('deleteConfirmModal')
	deleteConfirmModalTemplate!: TemplateRef<unknown>;

	protected searchText = '';
	protected hasError = false;
	protected errorMessage = '';
	get isLoading(): boolean {
		return this.loginService.IsLoadingUsers;
	}
	protected isAddingUser = false;
	protected isUpdatingUser = false;
	protected deletingUserId: string | null = null;

	// Sorting properties
	sortColumn:
		| 'name'
		| 'userName'
		| 'email'
		| 'organization'
		| 'permission'
		| null = null;
	sortDirection: 'asc' | 'desc' = 'asc';

	// Filter properties
	protected selectedOrganizationFilter: number | null = null;

	// Organization data
	protected organizations: OrgReturn[] = [];

	// Available permission types
	protected permissionTypes: Roles[] = Object.values(Roles).filter(
		(value) => typeof value === 'number' && value > -1,
	) as Roles[];

	protected readonly Roles: typeof Roles = Roles;

	errors: string[] = [];

	// New user form data
	protected newUser: {
		firstName: string;
		lastName: string;
		userName: string;
		email: string;
		password: string;
		vectorStoreId: string;
		permission: Roles;
		org: number;
	} = {
		firstName: '',
		lastName: '',
		userName: '',
		email: '',
		password: '',
		vectorStoreId: '',
		permission: Roles.User,
		org: 0,
	};

	// Password visibility state
	protected showNewUserPassword = false;
	protected showEditUserPassword = false;
	protected showEditUserOldPassword = false;

	// Email invite flag
	protected sendEmailInvite = false;

	// User being edited
	protected editingUser: (LoginInfo & { oldPassword?: string }) | null = null;

	private addUserModalRef: NgbModalRef | undefined;
	private editUserModalRef: NgbModalRef | undefined;
	private deleteConfirmModalRef: NgbModalRef | undefined;

	// User being deleted (for deletion confirmation modal)
	protected userToDelete: LoginInfo | null = null;

	constructor(
		protected loginService: LoginService,
		private modalService: NgbModal,
		private notificationService: NotificationService,
		private backendService: BackendService,
		private aiService: AiService,
		private cdr: ChangeDetectorRef,
	) {
		// Subscribe to user list updates
		this.loginService.OnUserList.subscribe(() => {
			// Users loaded - component will automatically reflect this through the isLoading getter
		});

		// Subscribe to organization list updates
		this.loginService.OnOrganizationList.subscribe((orgs: OrgReturn[]) => {
			if (orgs && orgs.length > 0) {
				this.organizations = orgs;
			} else {
				this.organizations = [];
			}
		});
	}

	ngOnInit(): void {
		// Lazy load users and organizations when component initializes
		this.loginService.ensureUsersLoaded().subscribe({
			next: (loaded) => {
				if (!loaded) {
					console.error('Failed to load users');
				}
			},
			error: (error) => {
				console.error('Error loading users:', error);
			},
		});

		// Also load organizations if user has permission
		this.loginService.ensureOrganizationsLoaded().subscribe({
			next: (loaded) => {
				if (!loaded) {
					console.error('Failed to load organizations');
				}
			},
			error: (error) => {
				console.error('Error loading organizations:', error);
			},
		});
	}

	get filteredUsers(): LoginInfo[] {
		let filtered = this.loginService.Users;

		// Apply organization filter
		if (this.selectedOrganizationFilter !== null) {
			filtered = filtered.filter(
				(user) =>
					user.organizationId === this.selectedOrganizationFilter,
			);
		}

		// Apply text search
		if (this.searchText && this.searchText.trim() !== '') {
			const searchTerms = this.searchText.toLowerCase().trim().split(' ');

			filtered = filtered.filter((user) => {
				const fullName =
					`${user.firstName} ${user.lastName}`.toLowerCase();
				const email = user.email.toLowerCase();
				const userName = user.userName.toLowerCase();
				const organizationName = this.getOrganizationName(
					user.organizationId,
				).toLowerCase();

				return searchTerms.every(
					(term) =>
						fullName.includes(term) ||
						email.includes(term) ||
						userName.includes(term) ||
						organizationName.includes(term),
				);
			});
		}

		// Apply sorting
		return this.applySorting(filtered);
	}

	/**
	 * Sorts the users by the specified column
	 */
	sortUsers(
		column: 'name' | 'userName' | 'email' | 'organization' | 'permission',
	): void {
		if (this.sortColumn === column) {
			// Toggle direction if clicking same column
			this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
		} else {
			// Set new column and start with descending if we were already ascending,
			// otherwise start with ascending
			this.sortColumn = column;
			this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
		}
	}

	/**
	 * Applies sorting to the given users array
	 */
	private applySorting(users: LoginInfo[]): LoginInfo[] {
		if (!this.sortColumn) {
			return users;
		}

		return [...users].sort((a, b) => {
			let aValue: string | number;
			let bValue: string | number;

			switch (this.sortColumn) {
				case 'name':
					aValue = `${a.firstName} ${a.lastName}`.toLowerCase();
					bValue = `${b.firstName} ${b.lastName}`.toLowerCase();
					break;
				case 'userName':
					aValue = a.userName.toLowerCase();
					bValue = b.userName.toLowerCase();
					break;
				case 'email':
					aValue = a.email.toLowerCase();
					bValue = b.email.toLowerCase();
					break;
				case 'organization':
					aValue = this.getOrganizationName(
						a.organizationId,
					).toLowerCase();
					bValue = this.getOrganizationName(
						b.organizationId,
					).toLowerCase();
					break;
				case 'permission':
					aValue = a.permission;
					bValue = b.permission;
					break;
				default:
					return 0;
			}

			let comparison = 0;
			if (aValue < bValue) {
				comparison = -1;
			} else if (aValue > bValue) {
				comparison = 1;
			}

			return this.sortDirection === 'asc' ? comparison : -comparison;
		});
	}

	/**
	 * Gets the sort icon class for a column
	 */
	getSortIcon(
		column: 'name' | 'userName' | 'email' | 'organization' | 'permission',
	): string {
		if (this.sortColumn !== column) {
			return 'bi-arrow-down-up'; // Default sort icon
		}
		return this.sortDirection === 'asc'
			? 'bi-sort-alpha-down'
			: 'bi-sort-alpha-up';
	}

	clearSearch(): void {
		this.searchText = '';
	}

	clearFilters(): void {
		this.searchText = '';
		this.selectedOrganizationFilter = null;
	}

	openAddUserModal(): void {
		this.errors = [];
		// Reset form
		this.newUser = {
			firstName: '',
			lastName: '',
			userName: '',
			email: '',
			password: '',
			vectorStoreId: '',
			permission: Roles.User,
			org: this.organizations.length > 0 ? this.organizations[0].id : 0,
		};
		// Reset password visibility
		this.showNewUserPassword = false;
		// Reset email invite flag
		this.sendEmailInvite = false;

		this.addUserModalRef = this.modalService.open(
			this.addUserModalTemplate,
			{
				centered: true,
			},
		);
	}

	// Store original user data for comparison
	private originalUserData: LoginInfo | null = null;

	openEditUserModal(user: LoginInfo): void {
		this.errors = [];
		// Store original user data for comparison
		this.originalUserData = user;
		this.editingUser = { ...user, oldPassword: '' };
		// Reset password visibility
		this.showEditUserPassword = false;
		this.showEditUserOldPassword = false;

		this.editUserModalRef = this.modalService.open(
			this.editUserModalTemplate,
			{
				centered: true,
			},
		);
	}

	addUser(): void {
		// Set loading state
		this.isAddingUser = true;

		// Call the dataService.Register method to add the user
		const userCreationRequest = {
			firstName: this.newUser.firstName,
			lastName: this.newUser.lastName,
			email: this.newUser.email,
			FirstName: this.newUser.firstName,
			Lastname: this.newUser.lastName,
			username: this.newUser.userName,
			password: this.sendEmailInvite ? '' : this.newUser.password,
			Role: this.newUser.permission,
			org: this.newUser.org,
		} as MpRegisterRequest;

		// Choose the appropriate API call based on sendEmailInvite flag
		const apiCall = this.sendEmailInvite
			? this.backendService.AddNewUserAndSendInvite(userCreationRequest)
			: this.backendService.AddNewUser(userCreationRequest);

		apiCall
			.pipe(
				catchError((response: HttpErrorResponse) => {
					const error = response.error as CommonErrorMessage;
					console.error('Error registering user:', error.message);
					this.notificationService.ShowToast(
						'Käyttäjän lisääminen epäonnistui. Tarkista tiedot ja yritä uudelleen.',
						'danger',
					);

					this.errors.push(`${error.title}: ${error.message}`);
					// Manually trigger change detection to update the modal
					this.cdr.detectChanges();

					// Clear loading state
					this.isAddingUser = false;

					return throwError(() => new Error(error.message));
				}),
				map((response: userReturn) => {
					// Success - registration worked
					// Create a new LoginInfo object from our form data
					const newUserInfo: LoginInfo = {
						firstName: this.newUser.firstName,
						lastName: this.newUser.lastName,
						userName: response.userName,
						email: this.newUser.email,
						password: '',
						vectorStoreId: this.newUser.vectorStoreId,
						permission: response.role,

						id: response.id,
						openAIApiKey: response.key,
					} as LoginInfo;

					return newUserInfo;
				}),
				switchMap((newUserInfo: LoginInfo) => {
					return this.loginService.RefreshUsers();
				}),
				switchMap(() => {
					// Refresh organizations to update user counts
					return this.loginService.RefreshOrganizations();
				}),
				tap(() => {
					// Let LoginService handle user list updates automatically
				}),
			)
			.subscribe({
				next: () => {
					// Clear loading state
					this.isAddingUser = false;

					// LoginService will handle user list updates automatically
					this.notificationService.ShowToast(
						`Käyttäjä ${this.newUser.firstName} ${this.newUser.lastName} lisätty`,
						'success',
					);

					if (this.addUserModalRef) {
						this.addUserModalRef.close();
					}
				},
				error: (response: any) => {
					// Clear loading state
					this.isAddingUser = false;

					console.error('Error adding user:', response);
					this.notificationService.ShowToast(
						'Käyttäjän lisääminen epäonnistui. Tarkista tiedot ja yritä uudelleen.',
						'danger',
					);
				},
			});
	}

	updateUser(): void {
		if (!this.editingUser || !this.originalUserData) return;

		// Set loading state
		this.isUpdatingUser = true;
		this.errors = [];

		const currentUser = this.loginService.GetCurrentUser();

		if (!currentUser) {
			this.notificationService.ShowToast(
				'Kirjaudu sisään päivittääksesi käyttäjätietoja.',
				'danger',
			);
			this.isUpdatingUser = false;
			return;
		}

		const oldPassword = this.editingUser.oldPassword;
		const user = this.editingUser;
		const original = this.originalUserData;

		// Build update request with only changed fields
		// Set empty string for unchanged fields, -1 for unchanged role
		const updateRequest: UserUpdateRequest = {
			oldPassword: oldPassword || '',
			newPassword: user.password || '',
			email: user.email !== original.email ? user.email : '',
			firstName:
				user.firstName !== original.firstName ? user.firstName : '',
			lastName: user.lastName !== original.lastName ? user.lastName : '',
			role:
				user.permission !== original.permission ? user.permission : -1,
		};

		this.backendService
			.UpdateUserInfo(user.id, updateRequest)
			.pipe(
				catchError((response: HttpErrorResponse) => {
					// Clear loading state
					this.isUpdatingUser = false;

					// Handle CommonErrorMessage format
					const error = response.error as CommonErrorMessage;
					console.error('Error updating user:', error);

					this.notificationService.ShowToast(
						'Käyttäjätietojen päivittäminen epäonnistui.',
						'danger',
					);

					this.errors = [];
					if (error?.title && error?.message) {
						// Display title and message from CommonErrorMessage
						this.errors.push(`${error.title}: ${error.message}`);
					} else if (
						response.error?.errors &&
						Array.isArray(response.error.errors)
					) {
						// Handle ErrorResponse format (legacy)
						response.error.errors.forEach((e: string) => {
							this.errors.push(e);
						});
					} else if (response.message) {
						this.errors.push(response.message);
					} else {
						this.errors.push(
							'Tuntematon virhe käyttäjätietojen päivityksessä.',
						);
					}

					// Manually trigger change detection to update the modal
					this.cdr.detectChanges();

					return throwError(
						() => new Error(error?.message || 'Update failed'),
					);
				}),
			)
			.subscribe({
				next: () => {
					// Clear loading state
					this.isUpdatingUser = false;

					// Update the local user data with the new values
					const userIndex = this.loginService.Users.findIndex(
						(u) => u.id === user.id,
					);
					if (userIndex !== -1) {
						// Update the user in the local array
						this.loginService.Users[userIndex].firstName =
							user.firstName;
						this.loginService.Users[userIndex].lastName =
							user.lastName;
						this.loginService.Users[userIndex].email = user.email;
						this.loginService.Users[userIndex].permission =
							user.permission;
					}

					this.notificationService.ShowToast(
						'Käyttäjätietojen päivittäminen onnistui.',
						'success',
					);
					if (this.editUserModalRef) {
						this.editUserModalRef.close();
					}
				},
				error: () => {
					// Error already handled in catchError
					// Just ensure loading state is cleared
					this.isUpdatingUser = false;
				},
			});
	}

	// Open delete confirmation modal
	openDeleteConfirmModal(user: LoginInfo): void {
		this.userToDelete = user;
		this.deleteConfirmModalRef = this.modalService.open(
			this.deleteConfirmModalTemplate,
			{
				centered: true,
			},
		);

		// Handle modal result
		this.deleteConfirmModalRef.result.then(
			(result) => {
				if (result === 'confirm') {
					this.deleteUser(user);
				}
				this.userToDelete = null;
			},
			() => {
				// Dismissed
				this.userToDelete = null;
			},
		);
	}

	// Delete user after confirmation
	private deleteUser(user: LoginInfo): void {
		// Set loading state for this specific user
		this.deletingUserId = user.id;

		this.backendService
			.DeleteUser(user.id)
			.pipe(
				catchError((error: ErrorResponse) => {
					console.error('Error deleting user:', error);
					this.notificationService.ShowToast(
						'Käyttäjän poistaminen epäonnistui.',
						'danger',
					);
					// Clear loading state
					this.deletingUserId = null;
					return throwError(() => error);
				}),
				switchMap(() => {
					return this.loginService.RefreshUsers();
				}),
			)
			.subscribe({
				next: () => {
					// Clear loading state
					this.deletingUserId = null;

					// LoginService will handle user list updates automatically via RefreshUsers() call above
					this.notificationService.ShowToast(
						`Käyttäjä ${user.firstName} ${user.lastName} poistettu`,
						'success',
					);
				},
				error: (error: ErrorResponse) => {
					// Clear loading state
					this.deletingUserId = null;

					console.error('Error deleting user:', error);
					this.notificationService.ShowToast(
						'Käyttäjän poistaminen epäonnistui.',
						'danger',
					);
				},
			});
	}

	// Helper method to check if we have modals open
	HasOtherModalsOpen(): boolean {
		return !!this.addUserModalRef || !!this.editUserModalRef;
	}

	// Helper method to check if a user is being deleted
	isUserBeingDeleted(userId: string): boolean {
		return this.deletingUserId === userId;
	}

	// Helper method to get organization name by ID
	getOrganizationName(orgId: number): string {
		const org = this.organizations.find((o) => o.id === orgId);
		return org ? org.name : '-';
	}

	// Generate a secure password
	generateSecurePassword(): string {
		const length = 16;
		const charset =
			'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+-=[]{}|;:,.<>?';
		let password = '';

		// Ensure at least one character from each required category
		const lowercase = 'abcdefghijklmnopqrstuvwxyz';
		const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
		const numbers = '0123456789';
		const symbols = '!@#$%^&*()_+-=[]{}|;:,.<>?';

		// Add one character from each category
		password += lowercase[Math.floor(Math.random() * lowercase.length)];
		password += uppercase[Math.floor(Math.random() * uppercase.length)];
		password += numbers[Math.floor(Math.random() * numbers.length)];
		password += symbols[Math.floor(Math.random() * symbols.length)];

		// Fill the rest randomly
		for (let i = password.length; i < length; i++) {
			password += charset[Math.floor(Math.random() * charset.length)];
		}

		// Shuffle the password to avoid predictable patterns
		return password
			.split('')
			.sort(() => Math.random() - 0.5)
			.join('');
	}

	// Generate password for new user form
	generatePasswordForNewUser(): void {
		this.newUser.password = this.generateSecurePassword();
		this.showNewUserPassword = true;
	}

	// Generate password for edit user form
	generatePasswordForEditUser(): void {
		if (this.editingUser) {
			this.editingUser.password = this.generateSecurePassword();
			this.showEditUserPassword = true;
		}
	}

	// Copy password to clipboard
	async copyPasswordToClipboard(password: string): Promise<void> {
		try {
			await navigator.clipboard.writeText(password);
			this.notificationService.ShowToast(
				'Salasana kopioitu leikepöydälle',
				'success',
			);
		} catch (err) {
			console.error('Failed to copy password:', err);
			this.notificationService.ShowToast(
				'Salasanan kopiointi epäonnistui',
				'danger',
			);
		}
	}

	// Toggle password visibility
	toggleNewUserPasswordVisibility(): void {
		this.showNewUserPassword = !this.showNewUserPassword;
	}

	toggleEditUserPasswordVisibility(): void {
		this.showEditUserPassword = !this.showEditUserPassword;
	}

	toggleEditUserOldPasswordVisibility(): void {
		this.showEditUserOldPassword = !this.showEditUserOldPassword;
	}

	// Check if current user requires old password for editing
	requiresOldPassword(): boolean {
		const currentUser = this.loginService.GetCurrentUser();
		return currentUser?.permission === Roles.User;
	}

	// Get display name for role
	getRoleDisplayName(role: Roles): string {
		switch (role) {
			case Roles.SuperAdmin:
				return 'Pääkäyttäjä';
			case Roles.Admin:
				return 'Organisaation pääkäyttäjä';
			case Roles.User:
				return 'Käyttäjä';
			default:
				return 'Tuntematon';
		}
	}
}
